import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Video } from "lucide-react";
import { LiveKitRoom } from "@livekit/components-react";
import "@livekit/components-styles";
import { api, formatApiError } from "../lib/api";
import { MeetingStage } from "../components/MeetingStage";

/**
 * Public laptop entry for MOBILE members: /meet/<CODE>. The code is
 * minted from the phone (POST /m/meetings/{id}/web-link) and resolves
 * to a LiveKit grant for the same room the lodge is using.
 */
export default function MeetWebJoinPage() {
  const { t } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [state, setState] = useState(code ? "loading" : "enter");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!code) return;
    setState("loading");
    api.get(`/meetings/web-join/${encodeURIComponent(code)}`)
      .then(({ data: d }) => { setData(d); setState(d?.livekit?.token ? "room" : "error"); if (!d?.livekit?.token) setError(t("webMeet.noLivekit", "Ședința nu poate fi deschisă în browser.")); })
      .catch((err) => { setError(formatApiError(err)); setState("error"); });
  }, [code]);

  if (state === "room" && data) {
    return (
      <div style={{ height: "100vh", background: "#0b1620" }} data-lk-theme="default" data-testid="web-meet-room">
        <div style={{ position: "absolute", top: 10, left: 14, zIndex: 5, color: "#e8d59a", fontFamily: "Cinzel, serif", fontSize: "0.8rem", letterSpacing: "0.14em" }} data-testid="web-meet-title">
          {data.title} · {data.display_name}
        </div>
        <LiveKitRoom
          serverUrl={data.livekit.url}
          token={data.livekit.token}
          connect
          audio
          video
          style={{ height: "100%" }}
          onDisconnected={() => setState("left")}
          onError={(e) => { setError(e?.message || "LiveKit error"); setState("error"); }}
        >
          <MeetingStage />
        </LiveKitRoom>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0b1620", display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }} data-testid="web-meet-page">
      <div className="gold-card" style={{ padding: "2rem", maxWidth: 440, width: "100%", textAlign: "center" }}>
        <Video size={40} color="#c9a84c" style={{ margin: "0 auto 0.8rem", display: "block" }} />
        <h1 style={{ fontFamily: "Cinzel, serif", fontSize: "1.2rem", color: "#0d1b2a", letterSpacing: "0.12em", marginBottom: "0.6rem" }}>
          {t("webMeet.title", "Ședință online")}
        </h1>
        {state === "loading" ? (
          <div style={{ color: "#9a7530" }} data-testid="web-meet-loading"><Loader2 className="animate-spin inline" size={22} /></div>
        ) : state === "left" ? (
          <p style={{ color: "#34322d" }} data-testid="web-meet-left">{t("webMeet.left", "Ai părăsit ședința.")}</p>
        ) : (
          <>
            {state === "error" ? <p style={{ color: "#7c2a2a", marginBottom: "0.8rem" }} data-testid="web-meet-error">{error}</p> : null}
            <p style={{ color: "#34322d", fontSize: "0.9rem", marginBottom: "0.8rem" }}>
              {t("webMeet.enterCode", "Introdu codul afișat în aplicația mobilă (Ședință → „Participă de pe laptop”).")}
            </p>
            <form
              onSubmit={(e) => { e.preventDefault(); const c = input.trim().toUpperCase(); if (c) navigate(`/meet/${c}`); }}
              style={{ display: "flex", gap: 8, justifyContent: "center" }}
            >
              <input
                className="input-lodge"
                style={{ textTransform: "uppercase", letterSpacing: "0.2em", textAlign: "center", maxWidth: 200 }}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="ABCD-1234"
                data-testid="web-meet-code-input"
              />
              <button type="submit" className="btn-primary-gold" data-testid="web-meet-code-submit">
                {t("webMeet.join", "INTRĂ")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
